import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { KanziRestClientService } from '@kanzi-apes/kanzi-rest-api';
import { EnvironmentService } from '@kanzi-apes/kanzi-utils';
import {
  BatchCreationRequestDto,
  BatchCreationResponseDto,
} from '../models';

/**
 * Servicio para operaciones de creación batch de ubicaciones
 * 
 * Este servicio maneja la comunicación con el endpoint backend
 * `/inventory/locations/batch-create/` para crear múltiples
 * ubicaciones en estructura jerárquica de forma masiva.
 * 
 * @example
 * ```typescript
 * const request: BatchCreationRequestDto = {
 *   parent_salon_id: 123,
 *   parent_salon_name: 'SALON-A',
 *   depth: 2,
 *   dimensions: { rows: 5, positions: 10 },
 *   prefixes: { row: 'F', position: 'P' },
 *   consecutive_format: 'numeric',
 *   customer_id: 456
 * };
 * 
 * this.batchService.createBatch(request).subscribe({
 *   next: (response) => {
 *     console.log(`Creadas ${response.total_created} ubicaciones`);
 *   },
 *   error: (error) => {
 *     console.error('Error al crear batch:', error);
 *   }
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class UbicacionBatchService {
  private readonly restClient = inject(KanziRestClientService);
  private readonly envService = inject(EnvironmentService);
  
  private baseUrl = '';
  private apiUrl = '';

  constructor() {
    if (this.envService.environment) {
      this.apiUrl = this.envService.environment.kongApi || '';
      this.baseUrl = `${this.apiUrl}${this.envService.environment.apiModules.inventory}`;
    }
  }

  /**
   * Crea un batch completo de ubicaciones
   * 
   * Envía la configuración al backend para crear múltiples ubicaciones
   * en una sola operación atómica. El backend maneja la creación de todas
   * las ubicaciones con transacciones, por lo que si alguna falla, todas
   * se revierten.
   * 
   * @param request - Configuración del batch a crear
   * @returns Observable con la respuesta del backend incluyendo:
   *   - total_created: Cantidad de ubicaciones creadas
   *   - breakdown_by_type: Desglose por tipo de ubicación
   *   - execution_time_seconds: Tiempo que tomó la creación
   *   - parent_salon: Información del salón padre
   *   - created_locations: Muestra de las ubicaciones creadas
   *   - statistics: Estadísticas de la configuración usada
   * 
   * @throws Error si el request body es inválido
   * @throws Error si el salón padre no existe
   * @throws Error si hay problemas de permisos
   * @throws Error si ocurre un error en el servidor
   * 
   * @example
   * ```typescript
   * // Crear batch de profundidad 2 (rows + positions)
   * const request: BatchCreationRequestDto = {
   *   parent_salon_id: 123,
   *   parent_salon_name: 'BODEGA-SUR',
   *   depth: 2,
   *   dimensions: {
   *     rows: 5,
   *     positions: 10
   *   },
   *   prefixes: {
   *     row: 'F',
   *     position: 'P'
   *   },
   *   consecutive_format: 'numeric',
   *   customer_id: 456
   * };
   * 
   * this.batchService.createBatch(request).subscribe({
   *   next: (response) => {
   *     // Total: 5 rows + 50 positions = 55 ubicaciones
   *     console.log(`Creadas ${response.total_created} ubicaciones en ${response.execution_time_seconds}s`);
   *   },
   *   error: (error) => {
   *     if (error.error?.error === 'VALIDATION_ERROR') {
   *       console.error('Error de validación:', error.error.details);
   *     }
   *   }
   * });
   * ```
   */
  createBatch(request: BatchCreationRequestDto): Observable<BatchCreationResponseDto> {
    const url = `${this.baseUrl}locations/batch-create/`;
    
    return this.restClient.post<BatchCreationResponseDto>(
      url,
      request,
      'Error al crear ubicaciones en batch'
    );
  }
}
